import type { ReactNode } from "react";
import type { SupportedCurrencyCode } from "@repo/api-schema";
import { formatMoney } from "../lib/money";
import { cn } from "../lib/utils";

interface ExpenseAmountProps {
  /** Expense total in the expense's own currency, minor units. */
  minor: number;
  /** Currency the expense was recorded in. */
  currency: SupportedCurrencyCode;
  /**
   * What the payers were actually charged in the group currency (FX + fees),
   * minor units — `Expense.groupCharge`. Only rendered when the currencies
   * differ, in which case it is the figure the group's balances move by.
   */
  groupCharge: number;
  groupCurrency: SupportedCurrencyCode;
  /** Classes for the primary (expense-currency) amount. */
  className?: string;
}

/**
 * An expense total for a list row: the amount as it was recorded, plus the
 * group-currency charge underneath whenever the two differ (issue #449). A
 * same-currency expense renders exactly as it did before — one line.
 *
 * The second line is the real `groupCharge`, not a client-side conversion, so
 * it matches what the group's balances actually moved by.
 */
export function ExpenseAmount({
  minor,
  currency,
  groupCharge,
  groupCurrency,
  className,
}: ExpenseAmountProps): ReactNode {
  const own = formatMoney(minor, currency);
  if (currency === groupCurrency) {
    return <span className={className}>{own}</span>;
  }

  const inGroupCurrency = formatMoney(groupCharge, groupCurrency);
  return (
    // One label for both lines: a screen reader reads "12.34 US dollars, 9.75
    // pounds in group currency" instead of two bare numbers.
    <span
      className="shrink-0 text-right"
      aria-label={`${own}, ${inGroupCurrency} in group currency`}
    >
      <span aria-hidden="true" className={cn("block", className)}>
        {own}
      </span>
      <span
        aria-hidden="true"
        className="block text-label-md text-on-surface-variant"
      >
        {inGroupCurrency}
      </span>
    </span>
  );
}
